import { FC, useEffect, useState } from "react"
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'
import { Box, Button, Card, Container, Typography } from "@mui/material";
import queryString from 'query-string'
const { parse } = queryString

import LayoutPlain from "./LayoutPlain";
import ActivateAccountForm from "@/components/authentication/ActivateAccountForm";
import { useDispatch, useSelector } from "react-redux";
import LoadingScreen from "@/components/LoadingScreen";
import ClientError from "../ClientError";
import { useResourceRegister } from "@/utils/resourceRegister";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { ReduxState } from "ra-core";
import { RegisterResponseDto } from "@/gen/api/user";


const ActivateAccount: FC = () => {
  useResourceRegister({ name: 'user-register-activate' })
  useResourceRegister({ name: 'autologin' })
  
  const validationResult = useSelector<ReduxState, any>(store => store.admin.resources['user-register-activate']?.data[-1])
  return (
    <CreateOrEdit 
      resource="user-register-activate" basePath="" 
      id="-1"
      record={validationResult}
    >
      <ActivateAccountBody />
    </CreateOrEdit>
  )
}

const ActivateAccountBody: FC = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [ states, setStates ] = useState({
    code: null as string | null,
    cid: null as string | null,
    email: null as string | null,
    errMsg: null as string | null,
  })

  const sendValidate = useSimpleSaveAction()

  useEffect(() => {
    if (!location.search) { return }
    setStates(v => {
      if (v.code) { return v }

      const queries = parse(location.search)
      const get = (x:string) => {
        const entry = queries[x]
        return !entry ? 'never'
        : entry instanceof Array ? entry[0]
        : entry
      }
      const code = get('code')
      const cid = get('cid')
      if (code) {
        sendValidate({ code, cid }, {
          onSuccess: ({data}: {data: RegisterResponseDto}) => {
            setStates(v1 => ({ ...v1, email: data.email || null, errMsg: null }))
          },
          onFailure: (err:any) => {
            setStates(v1 => ({ ...v1, errMsg: err.message }))
          }
        })
      }
      
      return { ...v, code, cid, }
    })
  }, [location.search, dispatch, sendValidate])

  

  // email was not decoded by backend from code yet
  if (!states.email) {
    // or, the code is invalid/expired
    if (states.errMsg) {
      return (
        <LayoutPlain>
          <ClientError 
            errorNumber={400}
            errorMsg={states.errMsg}
            shortTitle="Register"
            escapeEntry={
              <Button
                color="primary"
                component={RouterLink}
                to="/login"
                variant="outlined"
              >
                Take me to Sign In properly
              </Button>
            }
          />
        </LayoutPlain>
      )
    } else {
      return (
        <LoadingScreen />
      )
    }
  }
  return (
    <LayoutPlain>
      <Helmet>
        <title>Register | OnData</title>
      </Helmet>
      <Typography
        color="textPrimary"
        sx={{ 
          fontWeight: 'fontWeightBold',
          textAlign: 'center',
          pb: 3,
        }}
        variant="h5"
      >
        Thank you for joining OnData! Please choose a password for your account.
      </Typography>

      <Card
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            lg: 'repeat(2, 1fr)',
            xs: 'repeat(1, 1fr)'
          },
          minHeight: '100%'
        }}
      >
        <Box sx={{ pt: 8}} >
          <Container
            maxWidth="md"
            sx={{
              pl: {
                lg: 15
              }
            }}
          >
            <Typography
              sx={{ color: 'primary.main', pb: 3 }}
              variant="h6"
            >
              Password Guidelines
            </Typography>
            <Typography
              color="textPrimary"
              sx={{ py: 0.5 }}
              variant="body1"
            >
              1. Password must be 12 or more characters long.
            </Typography>
            <Typography
              color="textPrimary"
              sx={{ py: 0.5 }}
              variant="body1"
            >
              2. Password must contain uppercase and lowercase characters, numbers and special characters.
            </Typography>
          </Container>
        </Box>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            pt: 8,
            pb: 3,
          }}
        >
          <Container
            maxWidth="md"
            sx={{
              pr: {
                lg: 8
              }
            }}
          >
            <Box sx={{}}>
              <ActivateAccountForm code={states.code} email={states.email} cid={states.cid} />
            </Box>
          </Container>
        </Box>
      </Card>
    
    </LayoutPlain>
  )
}

export default ActivateAccount;
